/**
 * Costs-spezifische API-Funktionen (CAPEX/OPEX)
 * Investitions- und Betriebskosten - Item-basiertes Modell
 */
import { api } from "./client";
import {
  ProjectCosts,
  Capex,
  CapexItem,
  CapexItemCreate,
  CapexItemUpdate,
  Opex,
  OpexUpdate,
  OpexItem,
  OpexItemCreate,
  OpexItemUpdate,
  YearlyCostsResponse,
  ManualCostValue,
  ManualCostValueUpdate,
  LeaseTier,
  LeaseTierCreate,
  LeaseTierUpdate,
} from "../../types";

export const costsApi = {
  async getProjectCosts(projectId: string): Promise<ProjectCosts> {
    return api.get(`/projects/${projectId}/costs`);
  },

  // CAPEX Funktionen

  async getCapex(projectId: string): Promise<Capex> {
    return api.get(`/projects/${projectId}/costs/capex`);
  },

  async createCapexItem(
    projectId: string,
    data: CapexItemCreate,
  ): Promise<CapexItem> {
    return api.post(`/projects/${projectId}/costs/capex/items`, data);
  },

  /**
   * Aktualisiert eine CAPEX-Position
   * Wert kann immer geändert werden, Bezeichnung nur bei is_editable=true
   */
  async updateCapexItem(
    projectId: string,
    itemId: string,
    data: CapexItemUpdate,
  ): Promise<CapexItem> {
    return api.put(`/projects/${projectId}/costs/capex/items/${itemId}`, data);
  },

  /**
   * Löscht eine CAPEX-Position
   * Nur möglich bei is_editable=true
   */
  async deleteCapexItem(projectId: string, itemId: string): Promise<void> {
    return api.delete(`/projects/${projectId}/costs/capex/items/${itemId}`);
  },

  // OPEX Funktionen

  async getOpex(projectId: string): Promise<Opex> {
    return api.get(`/projects/${projectId}/costs/opex`);
  },

  /**
   * Aktualisiert die OPEX-Einstellungen (z.B. Steigerungsrate)
   */
  async updateOpex(projectId: string, data: OpexUpdate): Promise<Opex> {
    return api.put(`/projects/${projectId}/costs/opex`, data);
  },

  async createOpexItem(
    projectId: string,
    data: OpexItemCreate,
  ): Promise<OpexItem> {
    return api.post(`/projects/${projectId}/costs/opex/items`, data);
  },

  /**
   * Aktualisiert eine OPEX-Position
   * Wert/Modus kann immer geändert werden, Bezeichnung nur bei is_editable=true
   */
  async updateOpexItem(
    projectId: string,
    itemId: string,
    data: OpexItemUpdate,
  ): Promise<OpexItem> {
    return api.put(`/projects/${projectId}/costs/opex/items/${itemId}`, data);
  },

  /**
   * Löscht eine OPEX-Position
   * Nur möglich bei is_editable=true
   */
  async deleteOpexItem(projectId: string, itemId: string): Promise<void> {
    return api.delete(`/projects/${projectId}/costs/opex/items/${itemId}`);
  },

  // Jährliche Kosten Funktionen

  /**
   * Holt die berechneten jährlichen Kosten inkl. Abschreibungen
   */
  async getYearlyCosts(
    projectId: string,
    laufzeitJahre?: number,
  ): Promise<YearlyCostsResponse> {
    // Ohne laufzeitJahre → Backend nutzt project.laufzeit_jahre
    const query =
      laufzeitJahre !== undefined ? `?laufzeit_jahre=${laufzeitJahre}` : "";
    return api.get(`/projects/${projectId}/costs/yearly${query}`);
  },

  /**
   * Aktualisiert einen manuellen Jahreswert für eine OPEX-Position
   * Nur möglich wenn die Position den Modus 'manuell' hat
   */
  async updateYearlyValue(
    projectId: string,
    itemId: string,
    jahr: number,
    data: ManualCostValueUpdate,
  ): Promise<ManualCostValue> {
    return api.put(
      `/projects/${projectId}/costs/opex/items/${itemId}/yearly/${jahr}`,
      data,
    );
  },

  // Pachtkostenstaffeln (LeaseTier) Funktionen

  async getLeaseTiers(projectId: string): Promise<LeaseTier[]> {
    return api.get(`/projects/${projectId}/costs/opex/pacht/tiers`);
  },

  /**
   * Erstellt eine neue Pachtkostenstaffel
   * first_year muss größer sein als das first_year der letzten existierenden Staffel
   */
  async createLeaseTier(
    projectId: string,
    data: LeaseTierCreate,
  ): Promise<LeaseTier> {
    return api.post(`/projects/${projectId}/costs/opex/pacht/tiers`, data);
  },

  /**
   * Aktualisiert eine Pachtkostenstaffel
   * Hinweis: Das first_year der ersten Staffel kann nicht geändert werden
   */
  async updateLeaseTier(
    projectId: string,
    tierId: string,
    data: LeaseTierUpdate,
  ): Promise<LeaseTier> {
    return api.put(
      `/projects/${projectId}/costs/opex/pacht/tiers/${tierId}`,
      data,
    );
  },

  /**
   * Löscht eine Pachtkostenstaffel
   * Hinweis: Die erste Staffel (first_year=1) kann nicht gelöscht werden
   */
  async deleteLeaseTier(projectId: string, tierId: string): Promise<void> {
    return api.delete(
      `/projects/${projectId}/costs/opex/pacht/tiers/${tierId}`,
    );
  },

  /**
   * Setzt die manuelle Summe des Genehmigungs-Abschnitts oder entfernt sie (`wert: null`).
   *
   * Einziger Schreibpfad in diesen Abschnitt – seine Einzelpositionen kommen aus dem
   * Genehmigungsverfahren und sind read-only.
   */
  async setGenehmigungOverride(
    projectId: string,
    wert: number | null,
  ): Promise<import("../../types").Capex> {
    return api.put(`/projects/${projectId}/costs/capex/genehmigung/override`, {
      wert,
    });
  },

  async updateCapexSectionLabel(
    projectId: string,
    sectionKey: string,
    label: string,
  ): Promise<import("../../types").Capex> {
    return api.put(
      `/projects/${projectId}/costs/capex/sections/${sectionKey}/label`,
      { label },
    );
  },

  async updateOpexSectionLabel(
    projectId: string,
    sectionKey: string,
    label: string,
  ): Promise<import("../../types").Opex> {
    return api.put(
      `/projects/${projectId}/costs/opex/sections/${sectionKey}/label`,
      { label },
    );
  },
};
